import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import StatusBanner from '../ui/StatusBanner';
import { StatusPill } from '../ui/Status';
import Avatar from '../ui/Avatar';
import DirectorPanelState from './DirectorPanelState';
import { buildQuery } from '../../utils/buildQuery';
import { hasMultipleLocationGroups } from '../../utils/locationGrouping';

const APPROVAL_DOMAIN_OPTIONS = [
  { value: 'flagged_actual', label: 'Flagged Actuals' },
  { value: 'goal_proposal', label: 'Goal Proposals' },
  { value: 'goal', label: 'Legacy Goals' },
  { value: 'attendance_event', label: 'Attendance / PTO Events' },
  { value: 'performance_impact', label: 'Performance Impact' }
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'returned', label: 'Returned' }
];

function formatWords(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function getApprovalDomainLabel(value) {
  if (typeof value === 'object' && value?.approvalDomainLabel) {
    return value.approvalDomainLabel;
  }

  const labels = {
    flagged_actual: 'Flagged Actual',
    goal_proposal: 'Goal Proposal',
    goal: 'Goal Proposal',
    attendance_event: 'Attendance / PTO Event',
    performance_impact: 'Performance Impact'
  };

  return labels[String(value || '').toLowerCase()] || formatWords(value);
}

function getSubjectTypeLabel(value) {
  if (typeof value === 'object' && value?.subjectTypeLabel) {
    return value.subjectTypeLabel;
  }

  const labels = {
    manager_actual: 'Actual Record',
    location_actual: 'Location Actual Record',
    location_goal: 'Goal Record',
    manager_schedule_event: 'Attendance / PTO Event',
    inbound_sms: 'SMS PTO Notice'
  };

  return labels[String(value || '').toLowerCase()] || formatWords(value);
}

function getApprovalStatusLabel(value) {
  if (typeof value === 'object' && value?.statusLabel) {
    return value.statusLabel;
  }

  const labels = {
    pending: 'Pending Review',
    approved: 'Approved',
    denied: 'Denied',
    returned: 'Returned for Revision',
    cancelled: 'Cancelled'
  };

  return labels[String(value || '').toLowerCase()] || formatWords(value);
}

function getStatusTone(status) {
  const normalized = String(typeof status === 'object' ? status?.status : status || '').toLowerCase();
  return normalized === 'approved' ? 'active' : normalized === 'pending' ? 'warning' : 'inactive';
}

function getApprovalNote(item) {
  const metadata = item?.metadata || {};
  return (
    metadata.flagReason
    || metadata.proposalReason
    || metadata.performanceImpactReason
    || metadata.eventReason
    || item?.decisionNote
    || ''
  );
}

function formatGoalProposalValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'Not set';
}

function getAuthorityContextLabel(item) {
  if (item?.accessType === 'delegated' && item?.actingOnBehalfOfUserEmail) {
    return `Acting on behalf of ${item.actingOnBehalfOfUserEmail}`;
  }

  return 'Direct permissions';
}

function buildApprovalSummary(groups = []) {
  return groups.reduce((summary, group) => {
    group.items.forEach(item => {
      summary.all += 1;
      const key = String(item.status || '').toLowerCase();
      summary[key] = (summary[key] || 0) + 1;
    });
    return summary;
  }, {
    all: 0,
    pending: 0,
    approved: 0,
    denied: 0,
    returned: 0
  });
}

function buildDomainSummary(groups = []) {
  return groups.reduce((summary, group) => {
    group.items.forEach(item => {
      const key = String(item.approvalDomain || '').toLowerCase();
      summary[key] = (summary[key] || 0) + 1;
    });
    return summary;
  }, {});
}

function DirectorApprovalQueuePanel({
  selectedLocationCode = 'ALL',
  locationLabel = 'Company Rollup',
  user
}) {
  const [domainFilter, setDomainFilter] = useState('flagged_actual');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [locationFilter, setLocationFilter] = useState(selectedLocationCode !== 'ALL' ? selectedLocationCode : 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [approvalGroups, setApprovalGroups] = useState([]);
  const [selectedApprovalId, setSelectedApprovalId] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [pagination, setPagination] = useState({ totalCount: 0, visibleCount: 0, hasMore: false, offset: 0, limit: 50 });
  const activeRequestRef = useRef(0);

  useEffect(() => {
    setLocationFilter(selectedLocationCode !== 'ALL' ? selectedLocationCode : 'all');
  }, [selectedLocationCode]);

  const mergeGroups = (currentGroups, nextGroups) => {
    const mergedMap = new Map();
    [...currentGroups, ...nextGroups].forEach((group) => {
      const existing = mergedMap.get(group.locationId);
      if (!existing) {
        mergedMap.set(group.locationId, {
          ...group,
          items: [...(group.items || [])]
        });
        return;
      }

      const seen = new Set(existing.items.map(item => String(item.id)));
      (group.items || []).forEach((item) => {
        if (!seen.has(String(item.id))) {
          existing.items.push(item);
          seen.add(String(item.id));
        }
      });
    });

    return Array.from(mergedMap.values());
  };

  const queryIdentity = useMemo(() => JSON.stringify({
    companyId: user?.companyId || null,
    selectedLocationCode,
    domainFilter,
    statusFilter,
    locationFilter,
    searchTerm: searchTerm.trim()
  }), [domainFilter, locationFilter, searchTerm, selectedLocationCode, statusFilter, user?.companyId]);

  const loadApprovals = useCallback(async ({ keepCurrentSelection = true, append = false } = {}) => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setApprovalGroups([]);
      setSelectedApprovalId('');
      setDecisionNote('');
      setActionError('');
    }
    setError('');

    try {
      const scopedLocationCode = selectedLocationCode !== 'ALL'
        ? selectedLocationCode
        : (locationFilter !== 'all' ? locationFilter : '');
      const nextOffset = append ? pagination.offset + pagination.visibleCount : 0;
      const qs = buildQuery({
        approvalDomain: domainFilter,
        locationCode: scopedLocationCode,
        status: statusFilter !== 'all' ? statusFilter : null,
        search: searchTerm.trim(),
        limit: pagination.limit || 50,
        offset: nextOffset
      });

      const response = await apiRequest(`/api/approvals?${qs}`);
      if (activeRequestRef.current !== requestId) {
        return;
      }

      const groups = Array.isArray(response?.groups) ? response.groups : [];
      const nextGroups = append ? mergeGroups(approvalGroups, groups) : groups;
      const items = nextGroups.flatMap(group => group.items || []);
      setApprovalGroups(nextGroups);
      setPagination({
        totalCount: Number(response?.totalCount || 0),
        visibleCount: items.length,
        hasMore: Boolean(response?.hasMore),
        offset: Number(response?.offset || 0),
        limit: Number(response?.limit || 50)
      });

      if (!keepCurrentSelection || !items.some(item => String(item.id) === String(selectedApprovalId))) {
        setSelectedApprovalId(items[0] ? String(items[0].id) : '');
      }
    } catch (loadError) {
      if (activeRequestRef.current !== requestId) {
        return;
      }

      setError(loadError.message || 'Unable to load approval work items.');
      setApprovalGroups([]);
      setPagination(current => ({
        totalCount: 0,
        visibleCount: 0,
        hasMore: false,
        offset: 0,
        limit: current.limit || 50
      }));
      if (!keepCurrentSelection) {
        setSelectedApprovalId('');
      }
    } finally {
      if (activeRequestRef.current !== requestId) {
        return;
      }

      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [approvalGroups, domainFilter, locationFilter, pagination.limit, pagination.offset, pagination.visibleCount, searchTerm, selectedApprovalId, selectedLocationCode, statusFilter]);

  useEffect(() => {
    loadApprovals({ keepCurrentSelection: false });
    // Query identity intentionally drives the list refresh. loadApprovals also
    // depends on current rows for pagination, which should not retrigger fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryIdentity]);

  const approvalSummary = useMemo(() => buildApprovalSummary(approvalGroups), [approvalGroups]);
  const domainSummary = useMemo(() => buildDomainSummary(approvalGroups), [approvalGroups]);
  const showApprovalLocationHeaders = hasMultipleLocationGroups(approvalGroups);
  const allItems = useMemo(
    () => approvalGroups.flatMap(group => group.items || []),
    [approvalGroups]
  );
  const locationOptions = useMemo(
    () => approvalGroups.map(group => ({
      value: group.locationCode,
      label: `${group.locationCode} - ${group.locationName}`
    })),
    [approvalGroups]
  );
  const selectedApproval = useMemo(
    () => allItems.find(item => String(item.id) === String(selectedApprovalId)) || null,
    [allItems, selectedApprovalId]
  );

  useEffect(() => {
    if (isLoading || !selectedApprovalId || selectedApproval) {
      return;
    }

    setSelectedApprovalId('');
    setDecisionNote('');
    setActionError('');
  }, [isLoading, selectedApproval, selectedApprovalId]);

  const reviewNote = getApprovalNote(selectedApproval);
  const selfApprovalRestricted = selectedApproval && Number(selectedApproval.submittedByUserId) === Number(user?.id);
  const selfReviewHelpId = 'approval-self-review-help';
  const selfReviewHelpText = 'Self-review is disabled for this workflow type.';
  const pendingItems = useMemo(
    () => allItems.filter(item => String(item.status || '').toLowerCase() === 'pending'),
    [allItems]
  );
  const selectedPendingIndex = useMemo(
    () => pendingItems.findIndex(item => String(item.id) === String(selectedApprovalId)),
    [pendingItems, selectedApprovalId]
  );

  const handleDecision = useCallback(async (action) => {
    if (!selectedApproval || selfApprovalRestricted || selectedApproval.status !== 'pending') {
      return;
    }

    setIsSubmitting(true);
    setPendingAction(action);
    setActionError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/approvals/${selectedApproval.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify({
          decisionNote: decisionNote || undefined
        })
      });

      setBanner(`${getApprovalDomainLabel(updated.approvalDomain)} item marked ${getApprovalStatusLabel(updated.status)}.`);
      setDecisionNote('');
      await loadApprovals({ keepCurrentSelection: false });
      setSelectedApprovalId(String(updated.id));
    } catch (decisionError) {
      const message = decisionError.message || 'Unable to update approval item.';
      setActionError(message);
      if (/no longer needs review|auto-approved|already resolved|no longer exists/i.test(message)) {
        await loadApprovals({ keepCurrentSelection: false });
      }
    } finally {
      setIsSubmitting(false);
      setPendingAction('');
    }
  }, [decisionNote, loadApprovals, selectedApproval, selfApprovalRestricted]);

  const renderExpandedApprovalDetail = () => {
    if (!selectedApproval) {
      return null;
    }

    return (
      <div className="director-approval-detail director-approval-detail-inline">
        <div className="role-section-header director-approval-detail-inline-head">
          <div>
            <h3>Review Request</h3>
            <p>{selectedApproval.locationCode} · {getApprovalDomainLabel(selectedApproval.approvalDomain)}</p>
          </div>
          <StatusPill tone={getStatusTone(selectedApproval.status)}>
            {getApprovalStatusLabel(selectedApproval.status)}
          </StatusPill>
          {selfApprovalRestricted ? (
            <StatusPill
              tone="warning"
              className="director-approval-peer-review-chip"
              title="Awaiting peer/director review"
            >
              Awaiting peer/director review
            </StatusPill>
          ) : null}
        </div>

        <div className="director-approval-detail-grid">
          <div>
            <strong>Domain</strong>
            <p>{getApprovalDomainLabel(selectedApproval.approvalDomain)}</p>
          </div>
          <div>
            <strong>Subject</strong>
            <p>{getSubjectTypeLabel(selectedApproval.subjectType)}</p>
          </div>
          <div>
            <strong>Location</strong>
            <p>{selectedApproval.locationCode} - {selectedApproval.locationName}</p>
          </div>
          <div>
            <strong>Submitter</strong>
            <p className="director-detail-person">
              <Avatar
                email={selectedApproval.submittedByUserEmail}
                identifier={selectedApproval.submittedByUserId || selectedApproval.submittedByUserEmail || 'system'}
                name={selectedApproval.submittedByUserEmail || 'System'}
                size="sm"
                userId={selectedApproval.submittedByUserId}
              />
              <span className="full-text-value">{selectedApproval.submittedByUserEmail || 'System'}</span>
            </p>
          </div>
          <div>
            <strong>Status</strong>
            <p>{getApprovalStatusLabel(selectedApproval.status)}</p>
          </div>
          <div>
            <strong>Submitted</strong>
            <p>{selectedApproval.submittedAt ? new Date(selectedApproval.submittedAt).toLocaleString() : 'n/a'}</p>
          </div>
          <div>
            <strong>Reviewed By</strong>
            <p>{selectedApproval.reviewedByUserEmail || 'Pending Review'}</p>
          </div>
          <div>
            <strong>Reviewed At</strong>
            <p>{selectedApproval.reviewedAt ? new Date(selectedApproval.reviewedAt).toLocaleString() : 'Pending Review'}</p>
          </div>
        </div>

        <div className="director-approval-detail-copy">
          <strong>Flag / Review Note</strong>
          <p>{reviewNote || 'No note was provided for this approval item.'}</p>
        </div>

        {selectedApproval.approvalDomain === 'goal_proposal' ? (
          <div className="director-approval-detail-copy director-approval-goal-proposal">
            <strong>Goal Change</strong>
            <div className="director-approval-goal-proposal-grid">
              <span>
                <small>KPI</small>
                <b>{selectedApproval.metadata?.kpiLabel || selectedApproval.metadata?.kpiKey || 'Goal KPI'}</b>
              </span>
              <span>
                <small>Current goal</small>
                <b>{formatGoalProposalValue(selectedApproval.metadata?.currentGoalValue)}</b>
              </span>
              <span>
                <small>Proposed goal</small>
                <b>{formatGoalProposalValue(selectedApproval.metadata?.proposedGoalValue ?? selectedApproval.metadata?.goalValue)}</b>
              </span>
              <span>
                <small>Effective period</small>
                <b>{selectedApproval.metadata?.effectivePeriod || selectedApproval.metadata?.periodStart || 'Current period'}</b>
              </span>
            </div>
            <p>{selectedApproval.metadata?.proposalReason || 'No proposal reason provided.'}</p>
          </div>
        ) : null}

        {selectedApproval.subjectType === 'inbound_sms' && selectedApproval.metadata?.source === 'sms' && (
          <StatusBanner
            kind="warning"
            className="role-inline-message"
            message={(
              <>
                <strong>SMS PTO Notice</strong>
                {selectedApproval.metadata.managerName && (
                  <p style={{ margin: '4px 0 0', fontWeight: 400 }}>Manager: {selectedApproval.metadata.managerName}</p>
                )}
                {selectedApproval.metadata.fromNumber && (
                  <p style={{ margin: '2px 0 0', fontWeight: 400, fontSize: '0.85em' }}>From: {selectedApproval.metadata.fromNumber}</p>
                )}
                {selectedApproval.metadata.messageBody && (
                  <p style={{ margin: '6px 0 0', fontWeight: 400, fontStyle: 'italic' }}>"{selectedApproval.metadata.messageBody}"</p>
                )}
                <p style={{ margin: '6px 0 0', fontWeight: 400, fontSize: '0.85em' }}>
                  Approving will create a 1-day PTO event for the SMS received date. Denying will dismiss the notice.
                </p>
              </>
            )}
          />
        )}

        <div className="director-approval-detail-copy">
          <strong>Permissions Context</strong>
          <p>{getAuthorityContextLabel(selectedApproval)}</p>
          {selectedApproval.delegationMode ? (
            <p className="director-muted">
              Mode: {formatWords(selectedApproval.delegationMode)}
            </p>
          ) : null}
        </div>

        <StatusBanner
          kind="info"
          message={selfApprovalRestricted
            ? 'Waiting for another approver. You submitted this request, so self-review is restricted.'
            : ''}
          className="role-inline-message"
        />
        <StatusBanner
          kind="info"
          message={selectedApproval.accessType === 'delegated'
            ? `You are reviewing this item under delegated ${formatWords(selectedApproval.authorityType)} permissions for ${selectedApproval.locationCode}.`
            : ''}
          className="role-inline-message"
        />

        {pendingItems.length > 1 ? (
          <div className="director-table-actions">
            <Button
              size="sm"
              variant="secondary"
              disabled={selectedPendingIndex <= 0}
              onClick={() => setSelectedApprovalId(String(pendingItems[selectedPendingIndex - 1].id))}
            >
              Previous Pending
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={selectedPendingIndex === -1 || selectedPendingIndex >= pendingItems.length - 1}
              onClick={() => setSelectedApprovalId(String(pendingItems[selectedPendingIndex + 1].id))}
            >
              Next Pending
            </Button>
          </div>
        ) : null}
        <StatusBanner kind="error" message={actionError} className="role-inline-message" />
        <StatusBanner
          kind="loading"
          message={isSubmitting
            ? pendingAction === 'deny'
              ? 'Submitting denial…'
              : pendingAction === 'return'
              ? 'Returning item…'
              : 'Submitting approval…'
            : ''}
          className="role-inline-message"
        />

        <label className="role-review-note">
          Decision Note
          <textarea
            value={decisionNote}
            onChange={(event) => setDecisionNote(event.target.value)}
            placeholder="Optional note for approval history."
            disabled={isSubmitting || selectedApproval.status !== 'pending'}
          />
        </label>

        <div className="director-table-actions">
          <span title={selfApprovalRestricted ? selfReviewHelpText : undefined}>
            <Button
              size="sm"
              disabled={selectedApproval.status !== 'pending' || selfApprovalRestricted || (isSubmitting && pendingAction !== 'approve')}
              loading={isSubmitting && pendingAction === 'approve'}
              loadingLabel="Approving…"
              aria-describedby={selfApprovalRestricted ? selfReviewHelpId : undefined}
              onClick={() => handleDecision('approve')}
            >
              Approve
            </Button>
          </span>
          <span title={selfApprovalRestricted ? selfReviewHelpText : undefined}>
            <Button
              size="sm"
              variant="danger"
              disabled={selectedApproval.status !== 'pending' || selfApprovalRestricted || (isSubmitting && pendingAction !== 'deny')}
              loading={isSubmitting && pendingAction === 'deny'}
              loadingLabel="Denying…"
              aria-describedby={selfApprovalRestricted ? selfReviewHelpId : undefined}
              onClick={() => handleDecision('deny')}
            >
              Deny
            </Button>
          </span>
          <span title={selfApprovalRestricted ? selfReviewHelpText : undefined}>
            <Button
              size="sm"
              variant="secondary"
              disabled={selectedApproval.status !== 'pending' || selfApprovalRestricted || (isSubmitting && pendingAction !== 'return')}
              loading={isSubmitting && pendingAction === 'return'}
              loadingLabel="Returning…"
              aria-describedby={selfApprovalRestricted ? selfReviewHelpId : undefined}
              onClick={() => handleDecision('return')}
            >
              Return
            </Button>
          </span>
        </div>
        {selfApprovalRestricted ? (
          <p id={selfReviewHelpId} className="director-muted director-approval-self-review-help">
            {selfReviewHelpText}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="role-section director-approval-queue-section">
      <div className="role-section-header">
        <h2>Approval Queue</h2>
        <div className="role-section-actions">
          <span>{pagination.visibleCount} shown</span>
          <span>{pagination.totalCount} total</span>
        </div>
      </div>
      <p className="role-section-copy">
        Review approval work items for the current scope. The queue now supports flagged actuals, attendance/PTO event approvals, and separate performance-impact review decisions.
      </p>
      <p className="director-muted">
        Scope: {selectedLocationCode && selectedLocationCode !== 'ALL' ? locationLabel : 'Company Rollup'}
      </p>
      <DirectorPanelState kind="success" message={banner} />
      <DirectorPanelState kind="error" message={error} />

      <div className="director-approval-summary-row">
        {STATUS_FILTER_OPTIONS.filter(option => option.value !== 'all').map(option => (
          <div className="role-request-summary-card director-approval-summary-card" key={option.value}>
            <span>{option.label}</span>
            <strong>{approvalSummary[option.value] || 0}</strong>
          </div>
        ))}
      </div>

      <div className="director-approval-summary-row">
        {APPROVAL_DOMAIN_OPTIONS.map(option => (
          <div className="role-request-summary-card director-approval-summary-card" key={option.value}>
            <span>{option.label}</span>
            <strong>{domainSummary[option.value] || 0}</strong>
          </div>
        ))}
      </div>

      <div className="director-toolbar">
        <div className="director-toolbar-group">
          <label>Approval Domain</label>
          <div className="director-filter-chip-row">
            {APPROVAL_DOMAIN_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                className={`director-filter-chip ${domainFilter === option.value ? 'active' : ''}`}
                onClick={() => setDomainFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="director-toolbar-group">
          <label>Status</label>
          <div className="director-filter-chip-row">
            {STATUS_FILTER_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                className={`director-filter-chip ${statusFilter === option.value ? 'active' : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        {selectedLocationCode === 'ALL' ? (
          <div className="director-toolbar-group">
            <label htmlFor="approval-location-filter">Location</label>
            <select
              id="approval-location-filter"
              className="role-select"
              value={locationFilter}
              onChange={(event) => setLocationFilter(event.target.value)}
            >
              <option value="all">All Loaded Locations</option>
              {locationOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="director-toolbar-group director-approval-search-group">
          <label htmlFor="approval-search">Search</label>
          <input
            id="approval-search"
            className="role-select director-approval-search-input"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Submitter, location, note, subject..."
          />
        </div>
      </div>

      <div className="director-approval-list-shell">
        <div className="director-card director-approval-list-card">
          <div className="table-card">
            {isLoading ? (
              <div data-testid="approval-detail-loading">
                <table className="role-table director-approval-table" aria-label="Loading…">
                  <tbody>
                    {Array.from({ length: 5 }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="role-table-skeleton-row">
                        {Array.from({ length: 8 }).map((__, cellIndex) => (
                          <td key={cellIndex}><span className="role-skeleton-cell" /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <table className="role-table director-approval-table">
                <thead>
                  <tr>
                    <th className="director-approval-col-location">Location</th>
                    <th className="director-approval-col-domain">Domain</th>
                    <th className="director-approval-col-subject">Subject</th>
                    <th className="director-approval-col-submitter">Submitter</th>
                    <th className="director-approval-col-date">Submitted</th>
                    <th className="director-approval-col-status">Status</th>
                    <th>Flag / Review Note</th>
                    <th className="director-approval-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {approvalGroups.map(group => (
                    <React.Fragment key={group.locationId}>
                      {showApprovalLocationHeaders ? (
                        <tr className="director-location-group-row">
                          <td colSpan="8">
                            <span>{group.locationCode} - {group.locationName}</span>
                            <strong>{group.items.length}</strong>
                          </td>
                        </tr>
                      ) : null}
                      {group.items.map(item => {
                        const isSelected = String(selectedApprovalId) === String(item.id);
                        return (
                          <React.Fragment key={item.id}>
                            <tr
                              className={isSelected ? 'role-request-selected director-approval-row-expanded' : 'director-approval-clickable-row'}
                              onClick={() => {
                                setSelectedApprovalId(String(item.id));
                                setDecisionNote('');
                                setActionError('');
                              }}
                            >
                              <td className="director-approval-col-location">{item.locationCode}</td>
                              <td className="director-approval-col-domain">{getApprovalDomainLabel(item.approvalDomain)}</td>
                              <td className="director-approval-col-subject">{getSubjectTypeLabel(item.subjectType)}</td>
                              <td className="director-approval-col-submitter">
                                <span className="director-person-cell">
                                  <Avatar
                                    email={item.submittedByUserEmail}
                                    identifier={item.submittedByUserId || item.submittedByUserEmail || 'system'}
                                    name={item.submittedByUserEmail || 'System'}
                                    size="sm"
                                    userId={item.submittedByUserId}
                                  />
                                  <span className="full-text-value">{item.submittedByUserEmail || 'System'}</span>
                                </span>
                              </td>
                              <td className="director-approval-col-date">
                                {item.submittedAt ? new Date(item.submittedAt).toLocaleString() : 'n/a'}
                              </td>
                              <td className="director-approval-col-status">
                                <StatusPill tone={getStatusTone(item.status)}>
                                  {getApprovalStatusLabel(item.status)}
                                </StatusPill>
                              </td>
                              <td>
                                {getApprovalNote(item) || 'No note'}
                                {item.accessType === 'delegated' ? (
                                  <div className="director-approval-context-note">
                                    {getAuthorityContextLabel(item)}
                                  </div>
                                ) : null}
                              </td>
                              <td className="director-approval-col-action">
                                <Button
                                  variant="secondary"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedApprovalId(isSelected ? '' : String(item.id));
                                    setDecisionNote('');
                                    setActionError('');
                                  }}
                                >
                                  {isSelected ? 'Collapse' : 'Review'}
                                </Button>
                              </td>
                            </tr>
                            {isSelected ? (
                              <tr className="director-approval-expanded-row">
                                <td colSpan="8">
                                  {renderExpandedApprovalDetail()}
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  {approvalSummary.all === 0 ? (
                    <tr>
                      <td colSpan="8">
                        {searchTerm.trim()
                          ? 'No approval items match the current search and filters.'
                          : 'No approval items match the current scope and filters.'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )}
          </div>
          <div className="director-table-actions director-approval-footer-actions">
            {pagination.hasMore ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={isLoading}
                loading={isLoadingMore}
                loadingLabel="Loading…"
                onClick={() => loadApprovals({ keepCurrentSelection: true, append: true })}
              >
                Load More
              </Button>
            ) : null}
            {!pagination.hasMore && pagination.totalCount > 0 ? (
              <span className="director-muted">Showing all matching approval items currently loaded for this scope.</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DirectorApprovalQueuePanel;
